/* The drawer is morphed on every cart change, so listeners delegate off `document` and elements are re-resolved per click. */

import { CartLinesUpdateEvent } from '@shopify/events';

const ADD_SELECTOR = '[data-bronte-cart-add]';
const LABEL_SELECTOR = '[data-bronte-cart-add-label]';
const STATUS_SELECTOR = '[data-bronte-cart-add-status]';

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);
}

/* `/cart/add.js` answers with `text/javascript`, so parse the body text rather than calling `.json()`. */
async function parseCartResponse(response) {
  return JSON.parse(await response.text());
}

async function addToCart(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.disabled) return;

  const label = button.querySelector(LABEL_SELECTOR);
  const originalLabel = label?.textContent ?? '';

  button.disabled = true;

  const body = new FormData();
  body.set('id', variantId);
  body.set('quantity', '1');

  const sectionIds = cartSectionIds();
  if (sectionIds.length) body.set('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();

  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  // Theme is a script-scoped const in snippets/scripts.liquid, not a window property, so read it as a bare global.
  const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};

  try {
    const addResponse = await fetch(routes.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    const added = await parseCartResponse(addResponse);

    if (added.status) {
      // Shopify answers a rejected add with a status payload, usually a variant that sold out between render and click.
      const cartError = new Error(added.description || added.message || 'Add to cart failed');
      cartError.cartRejected = true;
      throw cartError;
    }

    const cartResponse = await fetch(`${routes.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    const ajaxCart = await parseCartResponse(cartResponse);

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        sections: added.sections,
        source: 'bronte-cart-drawer',
        itemCount: 1,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    console.warn('[bronte-cart-drawer] Add to cart failed:', error);

    button.disabled = false;
    if (label) label.textContent = originalLabel;

    // The card is re-rendered on the next cart change, so the notice only has to survive until then.
    if (error?.cartRejected) {
      const status = button.parentElement?.querySelector(STATUS_SELECTOR);
      if (status) status.hidden = false;
    }
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest(ADD_SELECTOR) : null;
  if (!target) return;

  event.preventDefault();
  addToCart(target);
});
